/// <reference path="../../../../typings/index.d.ts" />

module Meevo.Plugins.ClientIntake {
    import ICustomDataResource = Meevo.Model.ICustomDataResource;
    import FileTypes = Meevo.Model.FileTypes;

    const twoDigitNumber = (num: number): string => `0${num}`.slice(-2);
    export type SystemTagsTypes =
        | 'TextBox'
        | 'Notification'
        | 'Date'
        | 'CheckBox'
        | 'Label'
        | 'Select'
        | 'BirthDay'
        | 'Referred'
        | 'ViewImage'
        ;

    export type SysTagEntityType =
        | 'Client'
        | 'Guard'
        | 'clientNotificationOptions'
        | 'ClientAddress'
        | 'GuardianAddress'
        ;

    export interface ISystemTagModel {
        readonly value: any;
        readonly entity: SysTagEntityType;
        readonly key: string;
        readonly required: boolean;
        readonly disabled: boolean;
        isValid: boolean;

        prepare(): void;

        validate(): ng.IPromise<boolean>;

        addValidator(fn: systemTagValidatorFn): void;
    }

    interface INotificationValue {
        CommunicationType: number;
        IsEmailEnabled: boolean;
        IsSMSEnabled: boolean;
        SendDaysBefore?: number;
    }

    export interface IFormDataModel {
        value: string | ICustomDataResource;
        name: string;
        customDataResourceObject?: ICustomDataResource;
    }

    export interface IFileDataModel {
        value: ICustomDataResource;
        name: string;
        type: FileTypes;
        isRequired: boolean;
    }

    export interface IReferral {
        ownedByLocationId: number;
        atLocationId: number;
        totalLocations: number;
        availableAtLocations: number;
        objectState: number;
        objectCreationType: number;
        id: string;
        locationId: number;
        displayName: string;
    }

    export interface IIntakeFormSettings {
        clientIntakeFormsUrl: string;
    }

    export enum ReferredByEnum {
        Client = 1248,
        Employee = 1249,
        Referral = 1250,
    }

    // note: must return string error or null
    export type systemTagValidatorFn = (model: ISystemTagModel) => ng.IPromise<string>;

    const PHONE_REG = /^[0-9\-]*$/;
    const EMAIL_REG = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

    abstract class SystemTagBase implements ISystemTagModel {

        protected entityType: SysTagEntityType;

        public readonly abstract value: { [key: string]: any };
        public readonly required: boolean;
        public readonly disabled: boolean;

        public key: string;
        public isValid: boolean;

        private validators: systemTagValidatorFn[];

        public get entity() {
            return this.entityType;
        }

        constructor(protected element: HTMLElement, protected $q: ng.IQService) {
            const tagReg = new RegExp(/([\w\d\.]+):(\w+):(\w+)/);
            let full: string, key: string, entity: string, data: string, readOnly: string, required: string;
            try {
                [full, data, readOnly, required] = <any>tagReg.exec(element.dataset['options']);
                const rows: string[] = data.split('.');
                key = rows.slice(-1)[0];
                entity = rows.slice(0, -1).join('');
            } catch (e) {
                throw new Error(`Can't parse the options '${element.dataset['options']}'`);
            } finally {
                this.entityType = entity as SysTagEntityType;
                this.key = key;
                this.required = required === 'true';
                this.disabled = readOnly === 'true';
                this.validators = [];
                this.isValid = true;

                if (this.required) {
                    this.addValidator((model) => this.$q.resolve(!model.value[model.key] ? 'Field Required' : null));
                }

                this.element.addEventListener('focusout', () => this.validate());
            }
        }

        prepare(): void {
        }

        public validate(): ng.IPromise<boolean> {
            if (!this.validators.length) {
                this.hideError();
                this.isValid = true;

                return this.$q.resolve(this.isValid);
            }

            return this.$q.all(this.validators.map((validator) => validator(this)))
                .then((errors: string[]) => {
                    const error = errors.find((err) => !!err);

                    if (error) {
                        this.showError(error);
                        this.isValid = false;
                    } else {
                        this.hideError();
                        this.isValid = true;
                    }

                    return this.$q.resolve(this.isValid);
                })
                .catch((err) => this.$q.resolve(this.isValid));
        }

        public addValidator(fn: systemTagValidatorFn): void {
            if (typeof fn !== 'function') {
                return;
            }

            this.validators.push(fn);
        }

        private showError(error: string): void {
            if (this.element.className.indexOf('error') === -1) {
                this.element.className += ' error';
            }

            let errorEl = <HTMLElement>this.element.parentElement.querySelector('span.error');

            if (!errorEl) {
                errorEl = document.createElement('span');
                errorEl.className = 'error';
                this.element.parentElement.appendChild(errorEl);
            }

            errorEl.innerHTML = error;
            (errorEl).style.display = '';
        }

        private hideError(): void {
            this.element.className = this.element.className.replace('error', '').trim();

            let errorEl = <HTMLElement>this.element.parentElement.querySelector('span.error');
            if (!errorEl) {
                return;
            }

            errorEl.innerHTML = '';
            errorEl.style.display = 'none';
        }

    }

    export class TextBoxSystemTag extends SystemTagBase implements ISystemTagModel {

        constructor(element: HTMLElement, $q: ng.IQService) {
            super(element, $q);
            if (this.key === 'PrimaryPhone') {
                this.addValidator((tag) => {
                    if (!tag.value[tag.key] || PHONE_REG.test(tag.value[tag.key])) {
                        return this.$q.resolve(null);
                    }

                    return this.$q.resolve('Phone number format is invalid');
                });
            }

            if (this.key === 'EmailAddress') {
                this.addValidator((tag) => {
                    if (!tag.value[tag.key] || EMAIL_REG.test(tag.value[tag.key])) {
                        return this.$q.resolve(null);
                    }

                    return this.$q.resolve('Email format is invalid');
                });
            }
        }

        get value() {
            return {
                [this.key]: (<HTMLInputElement>this.element).value
            };
        }
    }

    export class NotificationsSystemTag extends SystemTagBase implements ISystemTagModel {

        protected communicationType: number;

        get entity() {
            return 'ClientNotificationOptions' as SysTagEntityType;
        }

        get value() {
            const value: INotificationValue = {
                CommunicationType: this.communicationType,
                IsEmailEnabled: (<HTMLInputElement>this.element.querySelector('[id$="-send-email"]')).checked,
                IsSMSEnabled: (<HTMLInputElement>this.element.querySelector('[id$="-send-text"]')).checked
            };
            const daysElement = this.element.querySelector('[id$="-days"]') as HTMLInputElement;
            if (daysElement) {
                value.SendDaysBefore = parseInt(daysElement.value, 10);
            }

            return {
                [this.key]: value
            };
        }

        constructor(element: HTMLElement, $q: ng.IQService) {
            super(element, $q);

            try {
                const data: { communication: number; } = JSON.parse(decodeURI(this.element.dataset['data']));
                this.communicationType = data.communication;
            } catch (e) {
            }

            const daysElement = this.element.querySelector('[id$="-days"]') as HTMLInputElement;
            if (daysElement) {
                daysElement.value = daysElement.dataset['value'];
            }
        }

        prepare() {
            _.forEach(this.element.getElementsByTagName('input'), (input: HTMLInputElement) => {
                input.checked = input.value === 'True';
            });
        }
    }

    export class DateSystemTag extends TextBoxSystemTag implements ISystemTagModel {

        get value() {
            const value = (this.element as HTMLInputElement).valueAsDate;

            return {
                [this.key]: value as any
            };
        }

        prepare() {
            // prepare value for the date input
            const value = (this.element as HTMLInputElement).value;
            if (value) {
                try {
                    const date = new Date(value);

                    (this.element as HTMLInputElement).value = `${date.getFullYear()}-${twoDigitNumber(date.getMonth() + 1)}-${twoDigitNumber(date.getDate())}`
                } catch (e) {
                    console.error(e);
                }
            }

            this.element.setAttribute('type', 'date');
        }
    }

    export class CheckBoxSystemTag extends SystemTagBase implements ISystemTagModel {

        get value() {
            return {
                [this.key]: (<HTMLInputElement>this.element.getElementsByTagName('input')[0]).checked
            };
        }

        prepare() {
            const value: string = (<HTMLInputElement>this.element.getElementsByTagName('input')[0]).value;
            (<HTMLInputElement>this.element.getElementsByTagName('input')[0]).checked = value === 'True';
        }
    }

    export class SelectSystemTag extends SystemTagBase implements ISystemTagModel {

        get value() {
            return {
                [this.key]: (<HTMLSelectElement>this.element.getElementsByTagName('select')[0]).value
            };
        }

        prepare() {
            const select: HTMLSelectElement = <HTMLSelectElement>this.element.getElementsByTagName('select')[0];
            select.value = select.dataset['value'];
        }
    }

    export class BirthDaySystemTag extends SystemTagBase implements ISystemTagModel {

        get value() {
            let data = {};


            _.forEach(this.element.getElementsByTagName('select'), (select: HTMLSelectElement) => {

                data[select.dataset['key']] = select.value
            });
            return data;
        }

        prepare() {
            _.forEach(this.element.getElementsByTagName('select'), (select: HTMLSelectElement) => {
                select.value = select.dataset['value'];
            });
        }
    }

    /**
     * ReferredSystemTag uses to show referral for the client.
     * If referral is client or employee we show only the display name and disable both blocks.
     * If referral is not set then we consider it as a Referral type and let them populate the display name of referral received from the server
     */
    export class ReferredSystemTag extends SelectSystemTag implements ISystemTagModel {

        public referralsListData: IReferral[] = [];

        get value() {
            return {
                [this.key]: (<HTMLSelectElement>this.element.getElementsByTagName('select')[0]).value,
                ReferredById: (<HTMLSelectElement>this.element.getElementsByTagName('select')[1]).value
            };
        }

        prepare() {
            const referredTypes: HTMLSelectElement = this.element.getElementsByTagName('select')[0];
            const referralsList: HTMLSelectElement = this.element.getElementsByTagName('select')[1];
            const referralDisplayName: HTMLInputElement = this.element.getElementsByTagName('input')[0];

            const referredTypesValue: ReferredByEnum = parseInt(referredTypes.dataset.value, 10) as ReferredByEnum || ReferredByEnum.Referral;
            const referralsValue: string = referralsList.dataset.value;

            referredTypes.value = referredTypesValue.toString();
            referredTypes.style.display = 'none';

            if (referredTypesValue !== ReferredByEnum.Referral) {
                this.element.style.display = 'none';

                return;
            }

            referralDisplayName.style.display = 'none';
            referralsList.innerHTML = this.referralsListData.map((row) => `<option value="${row.id}">${row.displayName}</option>`).join('');
            referralsList.value = referralsValue;
        }
    }

    export class ViewImageSystemTag extends SystemTagBase implements ISystemTagModel {
        get value() {
            return {[this.key]: null};
        }
    }

}
